import type {MarkdownEditorConfig} from '@github-ui/markdown-editor'
import {noop} from '@github-ui/noop'
import type {ReactionViewerGroups$key, ReactionViewerUser$key} from '@github-ui/reaction-viewer'
import type React from 'react'
import {useCallback, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueComment_issueComment$key} from './__generated__/IssueComment_issueComment.graphql'
import {IssueCommentEditor} from './IssueCommentEditor'
import {IssueCommentViewer} from './IssueCommentViewer'

type Props = {
  comment: IssueComment_issueComment$key
  reactionGroups: ReactionViewerGroups$key
  viewer: ReactionViewerUser$key
  onLinkClick?: (event: MouseEvent) => void
  onChange?: () => void
  onEditCancel?: () => void
  onReply?: (quotedComment: string) => void
  onSave?: () => void
  markdownEditorConfig?: MarkdownEditorConfig
  refAttribute?: React.RefObject<HTMLDivElement>
  highlightedCommentId?: string
  navigate: (url: string) => void
}

export function IssueComment({
  comment,
  reactionGroups,
  viewer,
  navigate,
  onLinkClick,
  onChange = noop,
  onEditCancel = noop,
  onReply = noop,
  onSave = noop,
  markdownEditorConfig,
  refAttribute,
  highlightedCommentId,
}: Props) {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const data = useFragment(
    graphql`
      fragment IssueComment_issueComment on IssueComment {
        ...IssueCommentViewerCommentRow
        ...IssueCommentEditor_repository
      }
    `,
    comment,
  )
  const commentSaved = useCallback(() => {
    setIsEditing(false)
    onSave()
  }, [onSave])

  const onCancel = () => {
    setIsEditing(false)
    onEditCancel()
  }

  return (
    <>
      {isEditing || isSubmitting ? (
        <IssueCommentEditor
          setIsSubmitting={setIsSubmitting}
          isSubmitting={isSubmitting}
          cancelEdit={onCancel}
          onSave={commentSaved}
          comment={data}
          repo={data}
          onChange={onChange}
          markdownEditorConfig={markdownEditorConfig}
        />
      ) : (
        <IssueCommentViewer
          comment={data}
          reactionGroups={reactionGroups}
          viewer={viewer}
          setIsEditing={() => setIsEditing(true)}
          onReply={onReply}
          onLinkClick={onLinkClick}
          refAttribute={refAttribute}
          navigate={navigate}
          highlightedCommentId={highlightedCommentId}
        />
      )}
    </>
  )
}

try{ IssueComment.displayName ||= 'IssueComment' } catch {}